import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  Box,
  Paper,
  Typography,
  TextField,
  Button,
  Alert,
  CircularProgress,
  Container,
  Card,
  CardContent,
  CardHeader
} from '@mui/material';
import { Lock as LockIcon, CheckCircle as CheckIcon } from '@mui/icons-material';
import axios from '../../config/axios';

const ActivateAccount: React.FC = () => {
  const { token } = useParams<{ token: string }>();
  const navigate = useNavigate();
  
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState(false);
  const [tokenValid, setTokenValid] = useState<boolean | null>(null);

  useEffect(() => {
    // Validate token on component mount
    if (token) {
      validateToken();
    }
  }, [token]);

  const validateToken = async () => {
    try {
      setLoading(true);
      // You could add a token validation endpoint if needed
      // For now, we'll assume the token is valid if it exists
      setTokenValid(true);
    } catch (error) {
      setTokenValid(false);
      setError('Neplatný nebo vypršený aktivační odkaz');
    } finally {
      setLoading(false);
    }
  };

  const handleActivate = async () => {
    if (!token) {
      setError('Chybí aktivační token');
      return;
    }

    if (password !== confirmPassword) {
      setError('Hesla se neshodují');
      return;
    }

    if (password.length < 6) {
      setError('Heslo musí mít alespoň 6 znaků');
      return;
    }

    try {
      setLoading(true);
      setError('');

      const response = await axios.post('/auth/activate', {
        token,
        password
      });

      setSuccess(true);
      
      // Redirect to login after 3 seconds
      setTimeout(() => {
        navigate('/login');
      }, 3000);

    } catch (error: any) {
      console.error('Activation error:', error);
      setError(error.response?.data?.error || 'Chyba při aktivaci účtu');
    } finally {
      setLoading(false);
    }
  };

  if (loading && tokenValid === null) {
    return (
      <Container maxWidth="sm">
        <Box sx={{ mt: 8, display: 'flex', justifyContent: 'center' }}>
          <CircularProgress />
        </Box>
      </Container>
    );
  }

  if (tokenValid === false) {
    return (
      <Container maxWidth="sm">
        <Box sx={{ mt: 8 }}>
          <Alert severity="error">
            Neplatný nebo vypršený aktivační odkaz. Kontaktujte administrátora.
          </Alert>
        </Box>
      </Container>
    );
  }

  if (success) {
    return (
      <Container maxWidth="sm">
        <Box sx={{ mt: 8 }}>
          <Card>
            <CardContent sx={{ textAlign: 'center', py: 4 }}>
              <CheckIcon sx={{ fontSize: 64, color: 'success.main', mb: 2 }} />
              <Typography variant="h5" gutterBottom>
                Účet úspěšně aktivován!
              </Typography>
              <Typography variant="body1" color="text.secondary">
                Váš účet byl úspěšně aktivován. Budete přesměrováni na přihlašovací stránku.
              </Typography>
            </CardContent>
          </Card>
        </Box>
      </Container>
    );
  }

  return (
    <Container maxWidth="sm">
      <Box sx={{ mt: 8 }}>
        <Card>
          <CardHeader
            title="Aktivace účtu"
            subheader="Nastavte si heslo pro váš účet"
            sx={{ textAlign: 'center' }}
          />
          <CardContent>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
              Vítejte v Invoice Factory! Pro dokončení registrace nastavte heslo pro váš účet.
            </Typography>

            {error && (
              <Alert severity="error" sx={{ mb: 2 }}>
                {error}
              </Alert>
            )}

            <TextField
              fullWidth
              label="Heslo"
              type="password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              margin="normal"
              required
              helperText="Heslo musí mít alespoň 6 znaků"
            />

            <TextField
              fullWidth
              label="Potvrzení hesla"
              type="password"
              value={confirmPassword}
              onChange={(e) => setConfirmPassword(e.target.value)}
              margin="normal"
              required
              error={password !== confirmPassword && confirmPassword !== ''}
              helperText={password !== confirmPassword && confirmPassword !== '' ? 'Hesla se neshodují' : ''}
            />

            <Box sx={{ mt: 3 }}>
              <Button
                fullWidth
                variant="contained"
                size="large"
                onClick={handleActivate}
                disabled={loading || !password || !confirmPassword || password !== confirmPassword}
                startIcon={loading ? <CircularProgress size={20} /> : <LockIcon />}
              >
                {loading ? 'Aktivuji účet...' : 'Aktivovat účet'}
              </Button>
            </Box>

            <Box sx={{ mt: 2, textAlign: 'center' }}>
              <Typography variant="body2" color="text.secondary">
                Již máte účet?{' '}
                <Button
                  variant="text"
                  size="small"
                  onClick={() => navigate('/login')}
                >
                  Přihlásit se
                </Button>
              </Typography>
            </Box>
          </CardContent>
        </Card>
      </Box>
    </Container>
  );
};

export default ActivateAccount;
